import * as React from 'react';
import { inject, observer } from 'mobx-react';
import styled from 'styled-components';
import { action, observable } from 'mobx';
import { IMAGE_NOT_FOUND_PLACEHOLDER } from '../../constants';
import { ISelectedImage } from '../../interface/component/selected-image.interface';
import { loop } from '../../util/number/loop';
import { getImageURL, IMAGE_SIZE_INTERVALS } from '../../util/url/get-image-url.util';
import { IInjectedProps } from '../../interface/component/injected-props.interface';
import { getCDNUrl } from '../../util/url/get-cdn-url.util';
import Loader from '../loaders/loader';
import { ImageSizeStyleEnum } from '../../enum/image-size-style.enum';
import { isArray } from 'lodash';

const IMAGE_WIDTH = 764;
const STARTING_IMAGE_WIDTH = IMAGE_SIZE_INTERVALS * 3;

interface ICmsImageProps {
  useOriginalImage?: boolean;
  renderAsImg?: boolean;
  src: ISelectedImage;
  alt?: string;
  contentStyle?: 'wide' | 'normal';
  title?: string;
  style?: React.CSSProperties;
  crop?: string;
  maxWidth?: number;
  maxHeight?: number;
  imagePercentage?: number; // 0 - 1
  showLoader?: boolean;
  className?: string;
  blockMentionData?: any;
  sizeStyle?: ImageSizeStyleEnum;
}

interface IStyledImageProps {
  src: { minWidth: number, maxWidth: number, location: string }[] | string;
  errorImage?: string;
  sizeStyle: ImageSizeStyleEnum;
}

const StyledImageDiv = styled.div`
  width: 100%;
  height: 100%;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  display: inline-block;
  ${(props: IStyledImageProps) => isArray(props.src) ? props.src.map((src) => `@media (max-width: ${src.maxWidth}px) and (min-width: ${src.minWidth}px) {
    background-image: url(${src.location});
  }`) : `background-image: url(${props.src});`}
`;

interface ImageContainerProps {
  contentStyle: 'wide' | 'normal';
}

const StyledImgContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
`;
const BaseStyledImage = styled.img`
  max-width: 100%;
  width: ${(props: ImageContainerProps) => props.contentStyle === 'wide' ? 'auto' : `${IMAGE_WIDTH}px`};
  height: auto;
  display: inline-block;
`;

interface IStyledImgProps {
  minWidth?: number;
  maxWidth?: number;
  display?: string;
}

const StyledImg = styled(BaseStyledImage)`
  display: ${(props: IStyledImgProps) => props.display ? props.display : 'none'} !important;
  ${(props: IStyledImgProps) => props.maxWidth !== undefined && props.minWidth !== undefined ? `@media (max-width: ${props.maxWidth}px) and (min-width: ${props.minWidth}px) {
    display: block !important;
  }` : ''}
`;

@inject('state')
@observer
export class CmsImage extends React.Component<ICmsImageProps & IInjectedProps> {
  checkForLoadErrorTimeout: any;
  @observable imageLoadError: boolean;
  @observable loadingImage: boolean;
  @observable modalImageUrl: string;
  @observable dimensions: string;

  clearCheckForLoadErrorTimeout() {
    clearTimeout(this.checkForLoadErrorTimeout);
    this.checkForLoadErrorTimeout = null;
  }

  @action
  componentWillMount() {
    this.loadingImage = this.props.showLoader;
  }

  componentWillUnmount() {
    this.clearCheckForLoadErrorTimeout();
  }

  render() {
    let content = null;
    if (this.loadingImage && this.props.showLoader) {
      content = <Loader
        width={200}
        height={200}
      />;
    } else {
      content = this.props.renderAsImg ?
        <StyledImgContainer>
          {!this.props.maxWidth && !this.props.maxHeight && <StyledImg
            key={STARTING_IMAGE_WIDTH}
            minWidth={0}
            maxWidth={STARTING_IMAGE_WIDTH}
            alt={this.props.alt}
            className={this.props.className}
            style={this.props.style}
            title={this.props.title}
            contentStyle={this.props.contentStyle}
            src={this.imageLoadError ? IMAGE_NOT_FOUND_PLACEHOLDER : getCDNUrl(this.props.state.configuration, getImageURL(this.props.state, this.props.src, STARTING_IMAGE_WIDTH * (this.props.imagePercentage || 1), this.props.blockMentionData, this.props.useOriginalImage, this.props.crop).location)}
          />}
          {this.props.maxWidth || this.props.maxHeight ?
              <StyledImg
                  key={this.props.maxWidth}
                  alt={this.props.alt}
                  display="block"
                  className={this.props.className}
                  style={this.props.style}
                  title={this.props.title}
                  contentStyle={this.props.contentStyle}
                  src={this.imageLoadError ? IMAGE_NOT_FOUND_PLACEHOLDER : getCDNUrl(this.props.state.configuration, getImageURL(this.props.state, this.props.src, {width: this.props.maxWidth, height: this.props.maxHeight, useSizeInterval: false}, this.props.blockMentionData, this.props.useOriginalImage, this.props.crop).location)}
              />
          : loop(STARTING_IMAGE_WIDTH + IMAGE_SIZE_INTERVALS, IMAGE_SIZE_INTERVALS * 16, IMAGE_SIZE_INTERVALS).map((width) => {
            return <StyledImg
              key={width}
              minWidth={width - IMAGE_SIZE_INTERVALS}
              maxWidth={width}
              alt={this.props.alt}
              className={this.props.className}
              style={this.props.style}
              title={this.props.title}
              contentStyle={this.props.contentStyle}
              src={this.imageLoadError ? IMAGE_NOT_FOUND_PLACEHOLDER : getCDNUrl(this.props.state.configuration, getImageURL(this.props.state, this.props.src, width * (this.props.imagePercentage || 1), this.props.blockMentionData, this.props.useOriginalImage, this.props.crop).location)}
            />;
          })}
        </StyledImgContainer> :
        <StyledImageDiv
          sizeStyle={this.props.sizeStyle}
          className={this.props.className}
          style={this.props.style}
          title={this.props.title}
          errorImage={this.imageLoadError ? IMAGE_NOT_FOUND_PLACEHOLDER : undefined}
          src={this.props.maxWidth || this.props.maxHeight ?
              getCDNUrl(this.props.state.configuration, getImageURL(this.props.state, this.props.src, {width: this.props.maxWidth, height: this.props.maxHeight, useSizeInterval: false}, this.props.blockMentionData, this.props.useOriginalImage, this.props.crop).location) :
              loop(STARTING_IMAGE_WIDTH + IMAGE_SIZE_INTERVALS, IMAGE_SIZE_INTERVALS * 16, IMAGE_SIZE_INTERVALS).map((width) => ({
                minWidth: width - IMAGE_SIZE_INTERVALS,
                maxWidth: width,
                location: getCDNUrl(this.props.state.configuration, getImageURL(this.props.state, this.props.src, width * (this.props.imagePercentage || 1), this.props.blockMentionData, this.props.useOriginalImage, this.props.crop).location)
              })).concat([ {
                minWidth: 0,
                maxWidth: STARTING_IMAGE_WIDTH,
                location: getCDNUrl(this.props.state.configuration, getImageURL(this.props.state, this.props.src, STARTING_IMAGE_WIDTH * (this.props.imagePercentage || 1), this.props.blockMentionData, this.props.useOriginalImage, this.props.crop).location)
              } ])}
        />;
    }
    return content;
  }
}
